import { TrashOutline } from 'heroicons-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FaArrowRotateRight } from 'react-icons/fa6';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import useServerArea from '../../../api/useServerArea';
import useServer from '../../../api/useServerMain';
import useServerProducts, { useServerProduct } from '../../../api/useServerProducts';
import AsyncComboBox from '../../../components/forms/AsyncCombobox';
import Input from '../../../components/forms/Input';
import MultiSelect from '../../../components/forms/Multiselect';
import Select from '../../../components/forms/Select';
import TextArea from '../../../components/forms/TextArea';
import ProductTypeBadge from '../../../components/misc/badges/ProductTypeBadge';
import Button from '../../../components/misc/Button';
import GenericTable from '../../../components/misc/GenericTable';
import { BtnActions } from '../../../components/misc/MultipleActBtn';
import Modal from '../../../components/modals/GenericModal';
import useProduct from '../../../hooks/useProduct';
import { SelectInterface } from '../../../interfaces/InterfacesLocal';
import {
    BasicNomenclator, TransformationRuleInterface
} from '../../../interfaces/ServerInterfaces';
import { useAppSelector } from '../../../store/hooks';
import { formatCurrency } from '../../../utils/helpers';
import { translateMeasure } from '../../../utils/translate';
import { DetailProductContext } from '../DetailProductContainer';

const TransformationRules = () => {
  const { product, updateProduct } = useContext(DetailProductContext)
  const { allTransformationRules, getAllTransformationRules, isLoading } =
    useServerProduct()
  const [transformationRuleModal, setTransformationRuleModal] = useState<{
    state: boolean
    current?: number
  }>({ state: false })

  useEffect(() => {
    getAllTransformationRules(product?.id!, { page: 1 })
  }, [])

  console.log(allTransformationRules)

  const tableTitles = [
    'Producto destino',
    'U/M Origen',
    'U/M Destino',
    'Equivalencia',
    'Áreas',
  ]

  const tableData =
    allTransformationRules?.map((item) => ({
      rowId: item.id,
      boldRow: item.id === 0,
      deletedRow: item.id === -1,
      payload: {
        'Producto destino': item?.finalProduct?.name ?? '',
        'U/M Origen': translateMeasure(item?.baseProduct?.measure) ?? '',
        'U/M Destino': translateMeasure(item?.finalProduct?.measure) ?? '',
        Equivalencia: item?.unitaryEquivalentFraction ?? '',
        Áreas: (
          <div>
            {item?.areas?.map((area) => (
              <p key={area.id} className="mr-2">
                {area.name}
              </p>
            )) ?? []}
          </div>
        ),
      },
    })) ?? []

  const onSubmit: SubmitHandler<Record<string, any>> = (data) => {
    const {
      printableLabelFront,
      printableLabelBack,
      expirationInDaysAfterProduction,
      productionOriginAreaId,
    } = data

    const dataToSend = {
      printableLabelFront,
      printableLabelBack,
      expirationInDaysAfterProduction,
      productionOriginAreaId,
    }

    updateProduct!(product?.id, dataToSend)
  }

  const actions: BtnActions[] = [
    {
      title: 'Nueva regla',
      action: () =>
        setTransformationRuleModal({
          state: true,
          current: undefined,
        }),
      icon: <PlusIcon className="h-5" />,
    },
  ]

  return (
    <div className="flex h-auto flex-col justify-start rounded-md border border-slate-300 p-7">
      <GenericTable
        tableTitles={tableTitles}
        tableData={tableData}
        loading={isLoading}
        actions={actions}
        rowAction={(rowId: number) =>
          setTransformationRuleModal({ state: true, current: rowId })
        }
      />
      {transformationRuleModal.state && (
        <Modal
          state={transformationRuleModal.state}
          close={() => setTransformationRuleModal({ state: false })}
        >
          <TransformationRulesModal
            transformationRule={allTransformationRules.find(
              (item) => item.id === transformationRuleModal.current
            )}
            close={() => setTransformationRuleModal({ state: false })}
            getAllTransformationRules={getAllTransformationRules}
          />
        </Modal>
      )}
    </div>
  )
}

export default TransformationRules

interface ModalProps {
  transformationRule?: TransformationRuleInterface
  close: () => void
  getAllTransformationRules: (productId: number, params?: any) => void
}
function TransformationRulesModal({
  transformationRule,
  close,
  getAllTransformationRules,
}: ModalProps) {
  const { register, handleSubmit, control } = useForm()
  const { product } = useContext(DetailProductContext)
  const {
    newTransformationRule,
    isLoading,
    deleteTransformationRule,
    updateTransformationRule,
    isFetching,
  } = useServerProducts()
  const { areas } = useAppSelector((state) => state.nomenclator)
  const { business } = useAppSelector((state) => state.init)
  const { getAllAreas, allAreas } = useServerArea()

  useEffect(() => {
    getAllAreas({ type: 'STOCK', all_data: true, inAllMyBusiness: true }, true)
  }, [])

  const areasSelect: SelectInterface[] =
    allAreas.length > 0
      ? (allAreas
          ?.filter((i) => i.type === 'STOCK')
          .map((a) => ({
            id: a?.id ?? null,
            name:
              a.business?.id === business!.id
                ? a?.name
                : (`${a?.name} (${a.business?.name})`),
          })) ?? [])
      : (areas
          .filter((i) => i.type === 'STOCK')
          .map((a) => ({
            id: a?.id ?? null,
            name: a?.name ?? '',
          })) ?? [])

  const onSubmit: SubmitHandler<Record<string, any>> = (data: any) => {
    if (!!transformationRule) {
      updateTransformationRule(transformationRule!.id, data, () => {
        close()
        getAllTransformationRules(product!.id, { page: 1 })
      })
    } else {
      newTransformationRule(data, product!.id, () => {
        close()
        getAllTransformationRules(product!.id, { page: 1 })
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex w-full items-center justify-between pe-6 text-center">
        {!!transformationRule ? (
          <h5 className="text-lg font-bold text-gray-600">
            Editar regla de transformación
          </h5>
        ) : (
          <h5 className="text-lg font-bold text-gray-600">
            Nueva regla de transformación
          </h5>
        )}
      </div>
      <AsyncComboBox
        label="Producto"
        name="finalProductId"
        control={control}
        // id : cuba
        dataQuery={{
          url: '/administration/product',
        }}
        normalizeData={{ id: 'id', name: 'name' }}
        defaultValue={transformationRule?.finalProduct.id ?? undefined}
        defaultItem={
          transformationRule
            ? {
                id: transformationRule.finalProduct.id,
                name: transformationRule.finalProduct.name,
              }
            : undefined
        }
        rules={{
          required: {
            value: true,
            message: 'Este campo es requerido',
          },
        }}
      />
      <Input
        label="Fracción unitaria de equivalencia"
        type="number"
        {...register('unitaryEquivalentFraction', { required: true })}
        control={control}
        defaultValue={transformationRule?.unitaryEquivalentFraction ?? ''}
      />
      <MultiSelect
        name={'listAreas'}
        label={'Áreas'}
        data={areasSelect}
        control={control}
        byDefault={transformationRule?.areas.map((area) => area.id) ?? []}
        defaultValue={transformationRule?.areas.map((area) => area.id) ?? []}
      />
      <div className="flex justify-end gap-2">
        {!!transformationRule && (
          <button
            className={
              'rounded-lg border border-red-500 p-2 transition-colors hover:bg-red-50'
            }
            onClick={() =>
              deleteTransformationRule(transformationRule.id, () => {
                close()
                getAllTransformationRules(product!.id, { page: 1 })
              })
            }
          >
            {isFetching ? (
              <div className={'flex items-center justify-center space-x-1'}>
                <FontAwesomeIcon
                  icon={faSpinner}
                  color={'#dc2626'}
                  aria-hidden="true"
                  className={'animate-spin'}
                  height={20}
                  width={20}
                />
                <p className={'text-xs text-red-500'}>Eliminando</p>
              </div>
            ) : (
              <div className={'flex items-center justify-center space-x-1'}>
                <TrashIcon color={'#dc2626'} height={20} width={20} />
                <p className={'text-xs text-red-500'}>Eliminar</p>
              </div>
            )}
          </button>
        )}
        <Button
          name={`${transformationRule ? 'Actualizar' : 'Crear'}`}
          color="slate-600"
          type="submit"
          loading={isLoading}
        />
      </div>
    </form>
  )
}
